import React, { useEffect, useState, useRef } from 'react'
import api from 'helpers/api'
import { useApplicationStore } from 'hooks/application'
import Select from 'components/select'
import { IIntegrationCatalogueField, FieldOption, IntegrationFieldSelectorProps } from 'types'

const IntegrationFieldSelector: React.FC<IntegrationFieldSelectorProps> = ({
  provider,
  fields,
  selectedValues,
  onFieldChange,
  type,
  compact = false
}) => {
  const { displayErrorMessage } = useApplicationStore()

  // State to hold options for each field (filtered to the current ancestor scope when options carry parentId)
  const [fieldOptions, setFieldOptions] = useState<Record<string, FieldOption[]>>({})
  // Unfiltered options as returned by the backend; used to resolve drilldown levels locally when options carry parentId
  const [allFieldOptions, setAllFieldOptions] = useState<Record<string, FieldOption[]>>({})
  // State to track loading status for each field
  const [loadingFields, setLoadingFields] = useState<Record<string, boolean>>({})
  // Cache for options to avoid refetching
  const [optionsCache, setOptionsCache] = useState<Record<string, FieldOption[]>>({})

  // Drilldown state: for each drilldown field, track multiple levels of options and selections
  // drilldownSelections[fieldKey][0] = first-level selection, [1] = sublevel 0, [2] = sublevel 1, etc.
  const [drilldownLevels, setDrilldownLevels] = useState<Record<string, FieldOption[][]>>({})
  const [drilldownSelections, setDrilldownSelections] = useState<Record<string, string[]>>({})
  const [drilldownLoading, setDrilldownLoading] = useState<Record<string, boolean>>({})

  // Track the drilldown parent that triggered the latest fetch to avoid stale updates
  const drilldownFetchRef = useRef<Record<string, string>>({})

  // Cache keys with a base-options fetch currently in flight. Guards the network
  // path against re-entry: the dependency-driven effect can re-run before a request
  // resolves (and, under a synchronous test act() flush, spin into an unbounded
  // re-render loop) — this dedupes to a single request per cache key. See #10460.
  const fetchInFlightRef = useRef<Set<string>>(new Set())

  // Sort fields by sortOrder
  const sortedFields = [...fields].sort((a, b) => a.sortOrder - b.sortOrder)

  // Check if a field's dependency is satisfied
  const isDependencySatisfied = (field: IIntegrationCatalogueField): boolean => {
    if (!field.dependsOn) {
      return true
    }
    const dependencyValues = selectedValues[field.dependsOn]
    return dependencyValues && dependencyValues.length > 0
  }

  // Whether a field is shown for the current sync type (mirrors the render gate).
  const isVisibleForType = (field: IIntegrationCatalogueField): boolean =>
    !field.visibleForTypes || field.visibleForTypes.includes(type)

  // Build a map of field keys to their configs for easy lookup
  const fieldsByKey: Record<string, IIntegrationCatalogueField> = {}
  fields.forEach((f) => {
    fieldsByKey[f.key] = f
  })

  // Get all ancestor dependency values by walking up the chain
  const getAncestorParams = (field: IIntegrationCatalogueField): Record<string, string> => {
    const params: Record<string, string> = {}
    let currentDep = field.dependsOn
    while (currentDep) {
      const depValue = selectedValues[currentDep]
      if (depValue && depValue.length > 0) {
        params[currentDep] = depValue[0]
      }
      const parentField = fieldsByKey[currentDep]
      currentDep = parentField?.dependsOn || null
    }
    return params
  }

  // Generate cache key for options based on field and all ancestor dependency values
  const getCacheKey = (field: IIntegrationCatalogueField): string => {
    const ancestorParams = getAncestorParams(field)
    const paramsStr = Object.entries(ancestorParams)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${k}=${v}`)
      .join('&')
    return `${provider}:${field.key}:${type}:${paramsStr}`
  }

  // Filter the full backend list down to options whose parentId matches the field's direct ancestor
  // (e.g. project_id for the Folder field). When options don't carry parentId, returns them unchanged.
  const filterToAncestor = (field: IIntegrationCatalogueField, options: FieldOption[]): FieldOption[] => {
    if (!options.some((o) => o.parentId !== undefined) || !field.dependsOn) {
      return options
    }
    const ancestorParams = getAncestorParams(field)
    const parentValue = ancestorParams[field.dependsOn]
    if (!parentValue) {
      return options
    }
    return options.filter((o) => o.parentId === parentValue)
  }

  // Fetch options for a specific field
  const fetchFieldOptions = async (field: IIntegrationCatalogueField) => {
    // Static fields carry their options in the catalogue — no network call.
    if (field.staticOptions && field.staticOptions.length > 0) {
      const staticOpts = field.staticOptions as FieldOption[]
      const current = allFieldOptions[field.key]
      // Compare option content (ids), not just length, so switching provider/type
      // to a same-key/same-count field with different options refreshes the list.
      const sameOptions =
        !!current &&
        current.length === staticOpts.length &&
        current.every((option, index) => option.id === staticOpts[index].id)
      if (!sameOptions) {
        setAllFieldOptions((prev) => ({ ...prev, [field.key]: staticOpts }))
        setFieldOptions((prev) => ({ ...prev, [field.key]: staticOpts }))
      }
      return
    }

    const cacheKey = getCacheKey(field)

    // Check cache first
    if (optionsCache[cacheKey]) {
      const cached = optionsCache[cacheKey]
      // If this cache entry is already the live value for the field, skip the
      // state updates — `filterToAncestor` would otherwise allocate a fresh
      // array each render and re-trigger the dep-driven effect.
      if (allFieldOptions[field.key] === cached) {
        return
      }
      setAllFieldOptions((prev) => ({ ...prev, [field.key]: cached }))
      setFieldOptions((prev) => ({ ...prev, [field.key]: filterToAncestor(field, cached) }))
      return
    }

    // A request for this cache key is already in flight — don't fire a duplicate
    // (and don't let the effect re-enter and loop before the first one resolves).
    if (fetchInFlightRef.current.has(cacheKey)) {
      return
    }
    fetchInFlightRef.current.add(cacheKey)

    setLoadingFields((prev) => ({ ...prev, [field.key]: true }))

    try {
      const params = getAncestorParams(field)
      params.type = type

      const response = await api.getProviderOptions(provider, field.key, params)
      const options = response.data

      setOptionsCache((prev) => ({
        ...prev,
        [cacheKey]: options
      }))

      setAllFieldOptions((prev) => ({
        ...prev,
        [field.key]: options
      }))
      setFieldOptions((prev) => ({
        ...prev,
        [field.key]: filterToAncestor(field, options)
      }))
      setLoadingFields((prev) => ({
        ...prev,
        [field.key]: false
      }))
      fetchInFlightRef.current.delete(cacheKey)
    } catch (error) {
      displayErrorMessage(error)
      // Cache the failure (empty options) under this cache key so the
      // dependency-driven effect doesn't re-fetch on every re-render — otherwise
      // a persistent error (e.g. Dalux's Box Pro 403) becomes a tight request
      // loop. Use one shared array reference for the cache and the live value so
      // the effect's cache-hit early-return fires on the next run. The fetch only
      // runs again once the inputs (and thus the cache key) change.
      const emptyOptions: FieldOption[] = []
      setOptionsCache((prev) => ({
        ...prev,
        [cacheKey]: emptyOptions
      }))
      setAllFieldOptions((prev) => ({
        ...prev,
        [field.key]: emptyOptions
      }))
      setFieldOptions((prev) => ({
        ...prev,
        [field.key]: emptyOptions
      }))
      setLoadingFields((prev) => ({
        ...prev,
        [field.key]: false
      }))
      fetchInFlightRef.current.delete(cacheKey)
    }
  }

  // Fetch options when dependencies change.
  // Gate on both isVisibleForType and isDependencySatisfied — the same condition as
  // the render — so we never fetch options for a field that isn't shown for the current
  // sync type (e.g. the Documents-only File Area field on a Dalux Inspection Plan Items
  // import, whose Box Pro–gated options endpoint 403s on non–Box Pro projects). See #10460.
  useEffect(() => {
    sortedFields.forEach((field) => {
      if (isVisibleForType(field) && isDependencySatisfied(field)) {
        fetchFieldOptions(field)
      }
    })
  }, [sortedFields, isVisibleForType, isDependencySatisfied, fetchFieldOptions])

  // Apply a field's `default` once, when it has static options and no selection yet.
  // Skip dependent fields so clearing a parent (which empties this value) doesn't
  // immediately bounce the default back. Skip fields hidden for the current type,
  // and only apply a default that is a real option, so we never persist a value
  // for an inapplicable field or an invalid option id.
  useEffect(() => {
    sortedFields.forEach((field) => {
      if (
        field.staticOptions &&
        field.default &&
        !field.dependsOn &&
        isVisibleForType(field) &&
        field.staticOptions.some((option) => option.id === field.default) &&
        (!selectedValues[field.key] || selectedValues[field.key].length === 0)
      ) {
        onFieldChange(field.key, [field.default])
      }
    })
  }, [sortedFields, selectedValues, onFieldChange, isVisibleForType])

  // Fetch drilldown children for a given parent value
  const fetchDrilldownChildren = async (field: IIntegrationCatalogueField, parentValue: string) => {
    // Local-filter path: when the L1 fetch returned options carrying parentId,
    // every nested level is just a filter on that cached list — no backend call.
    const cached = allFieldOptions[field.key] || []
    if (cached.some((o) => o.parentId !== undefined)) {
      const filtered = cached.filter((o) => o.parentId === parentValue)
      setDrilldownLevels((prev) => {
        const existing = prev[field.key] || []
        return {
          ...prev,
          [field.key]: [...existing.slice(0, -1), filtered]
        }
      })
      setDrilldownLoading((prev) => ({ ...prev, [field.key]: false }))
      return
    }

    const params = getAncestorParams(field)
    params.drilldown_parent = parentValue
    // Also include the field's own key for ancestor chain resolution
    params[field.key] = parentValue
    params.type = type

    drilldownFetchRef.current[field.key] = parentValue
    setDrilldownLoading((prev) => ({ ...prev, [field.key]: true }))

    try {
      const response = await api.getProviderOptions(provider, field.key, params)
      // Only apply if this is still the latest fetch for this field
      if (drilldownFetchRef.current[field.key] === parentValue) {
        const options = response.data
        setDrilldownLevels((prev) => {
          const existing = prev[field.key] || []
          // Replace the empty placeholder (last level) with actual options
          return {
            ...prev,
            [field.key]: [...existing.slice(0, -1), options]
          }
        })
        setDrilldownLoading((prev) => ({ ...prev, [field.key]: false }))
      }
    } catch (error) {
      displayErrorMessage(error)
      setDrilldownLoading((prev) => ({ ...prev, [field.key]: false }))
    }
  }

  // Collect all descendant fields recursively (children, grandchildren, etc.)
  const getDescendantFields = (parentKey: string): IIntegrationCatalogueField[] => {
    const descendants: IIntegrationCatalogueField[] = []
    const directChildren = fields.filter((f) => f.dependsOn === parentKey)
    directChildren.forEach((child) => {
      descendants.push(child)
      descendants.push(...getDescendantFields(child.key))
    })
    return descendants
  }

  // Reset value, options, cache, and drilldown state for every descendant of `parentKey`.
  // Shared by every parent-changing handler so drilldown selections never leave stale
  // allFieldOptions / optionsCache entries on dependent fields.
  const clearDescendantState = (parentKey: string) => {
    const descendants = getDescendantFields(parentKey)
    if (descendants.length === 0) {
      return
    }
    const descendantKeys = new Set(descendants.map((d) => d.key))
    const drilldownKeys = new Set(descendants.filter((d) => d.drilldown).map((d) => d.key))

    descendants.forEach((depField) => {
      onFieldChange(depField.key, [])
    })

    const removeKeys = (state: Record<string, FieldOption[]>) => {
      const updated = { ...state }
      descendantKeys.forEach((key) => delete updated[key])
      return updated
    }
    setFieldOptions(removeKeys)
    setAllFieldOptions(removeKeys)

    setOptionsCache((prev) => {
      const updated: Record<string, FieldOption[]> = {}
      Object.entries(prev).forEach(([cacheKey, cachedOptions]) => {
        if (!descendants.some((d) => cacheKey.includes(`:${d.key}:`))) {
          updated[cacheKey] = cachedOptions
        }
      })
      return updated
    })

    if (drilldownKeys.size > 0) {
      const removeDrilldown = <T,>(state: Record<string, T>) => {
        const updated = { ...state }
        drilldownKeys.forEach((key) => delete updated[key])
        return updated
      }
      setDrilldownLevels(removeDrilldown)
      setDrilldownSelections(removeDrilldown)
      // Invalidate any in-flight drilldown fetch for these descendants so a late
      // response can't repopulate cleared levels (its parentValue check fails).
      setDrilldownLoading(removeDrilldown)
      drilldownKeys.forEach((key) => delete drilldownFetchRef.current[key])
    }
  }

  // Handle field value change (for non-drilldown fields)
  const handleFieldChange = (field: IIntegrationCatalogueField, value: string) => {
    const isMultiselect = field.type === 'multiselect'
    const currentValues = selectedValues[field.key] || []

    let newValues: string[]
    if (isMultiselect) {
      // Toggle value in multiselect
      if (currentValues.includes(value)) {
        newValues = currentValues.filter((v) => v !== value)
      } else {
        newValues = [...currentValues, value]
      }
    } else {
      // Single select - replace value
      newValues = [value]
    }

    // Call the change handler
    onFieldChange(field.key, newValues)
    clearDescendantState(field.key)
  }

  // Handle multiselect setOptionsSelected
  const handleSetOptionsSelected = (field: IIntegrationCatalogueField, values: string[]) => {
    onFieldChange(field.key, values)
    clearDescendantState(field.key)
  }

  // Handle selection on the first level of a drilldown field (from the base fetch)
  const handleDrilldownFirstLevel = (field: IIntegrationCatalogueField, value: string) => {
    // Update the field value to the selected folder
    onFieldChange(field.key, [value])

    // Reset drilldown levels with an empty placeholder for incoming children
    setDrilldownLevels((prev) => ({
      ...prev,
      [field.key]: [[]]
    }))
    setDrilldownSelections((prev) => ({
      ...prev,
      [field.key]: [value]
    }))

    clearDescendantState(field.key)

    // Fetch children for drilldown
    fetchDrilldownChildren(field, value)
  }

  // Handle selection at a drilldown sublevel
  const handleDrilldownLevelSelect = (field: IIntegrationCatalogueField, levelIndex: number, value: string) => {
    // Update the field value to the deepest selected folder
    onFieldChange(field.key, [value])

    // Truncate levels beyond current and add empty placeholder for incoming children
    setDrilldownLevels((prev) => {
      const levels = prev[field.key] || []
      return {
        ...prev,
        [field.key]: [...levels.slice(0, levelIndex + 1), []]
      }
    })

    // Store at levelIndex + 1 (index 0 is the first-level selection)
    setDrilldownSelections((prev) => {
      const selections = [...(prev[field.key] || [])]
      selections[levelIndex + 1] = value
      return {
        ...prev,
        [field.key]: selections.slice(0, levelIndex + 2)
      }
    })

    clearDescendantState(field.key)

    // Fetch children for the next drilldown level
    fetchDrilldownChildren(field, value)
  }

  return (
    <div>
      {sortedFields.map((field) => {
        if (!isVisibleForType(field)) {
          return null
        }

        const isVisible = isDependencySatisfied(field)
        if (!isVisible) {
          return null
        }

        const options = fieldOptions[field.key] || []
        const loading = loadingFields[field.key] || false
        const selected = selectedValues[field.key] || []
        const isMultiselect = field.type === 'multiselect'
        const hasGroups = options.some((o) => o.group)

        return (
          <div key={field.key} data-testid={`integration-field-${field.key}`}>
            <div style={{ marginTop: compact ? '20px' : '30px' }}>
              {/* In compact mode match the Select's own label style (text-muted text-sm)
                  so the field headers line up with the Data Source / Data to Import labels. */}
              <div
                className={`flex ${compact ? 'text-muted text-sm' : 'font-bold'}`}
                style={{ marginBottom: compact ? '5px' : '10px' }}
              >
                {field.label}
                {field.required && <span className="text-red">*</span>}
              </div>
              <Select
                options={options
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((option) => ({
                    label: option.name,
                    value: option.id,
                    group: option.group,
                    disabled: option.disabled
                  }))}
                onOptionClick={(value) =>
                  field.drilldown ? handleDrilldownFirstLevel(field, value) : handleFieldChange(field, value)
                }
                optionsSelected={
                  field.drilldown && drilldownSelections[field.key]?.[0]
                    ? [drilldownSelections[field.key][0]]
                    : selected
                }
                loading={loading}
                multiselect={isMultiselect}
                groupBy={hasGroups}
                setOptionsSelected={isMultiselect ? (values) => handleSetOptionsSelected(field, values) : undefined}
                placeholder={`Select ${field.label.toLowerCase()}`}
              />
            </div>

            {/* Render drilldown sub-levels */}
            {field.drilldown &&
              (drilldownLevels[field.key] || []).map((levelOptions, levelIndex) => {
                const levelHasGroups = levelOptions.some((o) => o.group)
                return (
                  <div key={levelIndex} style={{ marginTop: '30px' }}>
                    <div className="font-bold flex" style={{ marginBottom: '10px' }}>
                      {field.label} (Level {levelIndex + 2})
                    </div>
                    <Select
                      options={levelOptions
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((option) => ({
                          label: option.name,
                          value: option.id,
                          group: option.group
                        }))}
                      onOptionClick={(value) => handleDrilldownLevelSelect(field, levelIndex, value)}
                      optionsSelected={
                        drilldownSelections[field.key]?.[levelIndex + 1]
                          ? [drilldownSelections[field.key][levelIndex + 1]]
                          : []
                      }
                      loading={
                        levelIndex === (drilldownLevels[field.key] || []).length - 1 &&
                        (drilldownLoading[field.key] || false)
                      }
                      groupBy={levelHasGroups}
                      placeholder={`Select ${field.label.toLowerCase()}`}
                    />
                  </div>
                )
              })}
          </div>
        )
      })}
    </div>
  )
}

export default IntegrationFieldSelector
